import { useCallback } from 'react';

import uniq from 'lodash-es/uniq';
import { useDispatch, useSelector } from 'react-redux';

import { updateContext } from '../redux/actions';
import { selectContext, selectCurrentTags } from '../redux/selectors';

/**
 * Reads/writes `context.tags` on the current (last) context entry of the
 * `tryIt` slice. Mirrors the legacy Try It tags behavior so a tag set here
 * is carried by the next test send (and stays in sync with the legacy modal).
 */
export const useContextTags = () => {
  const dispatch = useDispatch();
  const storeContext = useSelector(selectContext);
  const tags = useSelector(selectCurrentTags);

  // Edits target the last (current) context entry — that's what the next send
  // carries. `slice(0, -1)` drops the stale current; an empty stack seeds the
  // first entry.
  const writeTags = useCallback(
    (nextTags: string[]) => {
      const current = storeContext[storeContext.length - 1] ?? {};
      dispatch(updateContext([...storeContext.slice(0, -1), { ...current, tags: nextTags }]));
    },
    [dispatch, storeContext]
  );

  const addTag = useCallback(
    (tag: string) => {
      if (!tag) {
        return;
      }
      writeTags(uniq([...tags, tag]));
    },
    [tags, writeTags]
  );

  const removeTag = useCallback(
    (tag: string) => {
      writeTags(tags.filter((current) => current !== tag));
    },
    [tags, writeTags]
  );

  return { tags, addTag, removeTag };
};
